import { normalizeBandHref } from "@/app/components/SmileStickyHeader/normalizeBandHrefs";

/**
 * Entry-bound link rows for the /smile chrome atoms (Smile Header nav, Smile
 * Footer policy strip).
 *
 * Both atoms declare their link list as an Array variable pinned to
 * bindingSourceType ["entry"], bound in Studio to a TAG link container: the
 * header to a Navigation Group's Links field, the footer to a Footer
 * Navigation Group's Navigation Links For Group field. The Experiences SDK
 * (experiences-core 3.8.10, getArrayValue) resolves the field one level deep
 * and hands the component the level-1 entries in CDA REST shape - locale
 * stripped, each entry's own references left as Link stubs - or undefined
 * when the variable is unbound, the field is empty, or nothing resolved. It
 * never sends an empty array.
 *
 * This module turns that into what the atoms render: {label, href} rows,
 * plus the human-readable reason for every item it had to skip so the Studio
 * canvas can say so. Two content types are understood, the two those TAG
 * fields accept:
 *
 *   navigationLink  label = Link Text, href = External URL (or a resolved
 *                   Page Data path); Open In New Tab / Open In New Window
 *                   mark a new-tab link. The field's regexp wants a dotted
 *                   host, so a /smile link is stored production-absolute and
 *                   normalized here.
 *   page            label = breadcrumb title, else title; href = path (the
 *                   rule TAG's own Footer uses for the same field).
 *
 * Href hygiene runs on every row, bound or plain: production and staging web
 * origins come off so the link stays on whatever door the visitor is already
 * on (TAG-163 / TAG-167 - an atom renders from code and never passes through
 * normalize-slot-hrefs); the OneTrust sentinel and the [schedulingUrl] token
 * are refused because the atoms own those controls; and what is left must be
 * on the allowlist in refuseHref - a same-site path, a full https URL, a
 * mailto: or a tel: link - or it is refused with a reason for the canvas.
 */

/** One rendered row of a chrome link list. */
export interface SmileLinkItem {
  label: string;
  href: string;
  openInNewTab?: boolean;
}

/**
 * What an entry-bound Array variable hands the atom (an entry in CDA REST
 * shape, or a Link stub the SDK could not resolve), or the plain row a
 * caller or test passes directly.
 */
export type SmileLinkEntryInput =
  | {
      sys: {
        id?: string;
        type?: string;
        linkType?: string;
        contentType?: { sys?: { id?: string } };
      };
      fields?: Record<string, unknown>;
    }
  | { label?: string; href?: string; openInNewTab?: boolean };

interface EntryLike {
  sys: { id?: string; type?: string; linkType?: string; contentType?: { sys?: { id?: string } } };
  fields?: Record<string, unknown>;
}

type Mapped = { item: SmileLinkItem } | { reason: string };

/**
 * The staging door's origins. normalizeBandHref already strips the
 * production web origins; a staging URL typed into content would otherwise
 * ship as a staging link on production, the mirror image of TAG-163.
 */
const STAGING_WEB_ORIGINS = ["https://wwwstg.aspendental.com", "http://wwwstg.aspendental.com"];

/** Host of TAG's "Your Privacy Choices" Navigation Link; the footer's button owns that control. */
const ONETRUST_SENTINEL_HOST = "toggle-cookie-display.co";

/** TAG's header substitutes this token at render time; the atoms have no substitution for it. */
const SCHEDULING_URL_TOKEN = "[schedulingUrl]";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asText = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const isEntryLike = (value: unknown): value is EntryLike => isRecord(value) && isRecord(value.sys);

/** How the canvas names a skipped entry: its display name, else its text, else its id. */
const describeEntry = (entry: EntryLike): string => {
  const fields = entry.fields ?? {};
  return (
    asText(fields.name) ??
    asText(fields.linkText) ??
    asText(fields.title) ??
    asText(entry.sys.id) ??
    "unnamed entry"
  );
};

function mapEntry(entry: EntryLike): Mapped {
  const name = describeEntry(entry);
  const contentType = asText(entry.sys.contentType?.sys?.id);
  const fields = entry.fields ?? {};

  if (!contentType) {
    return {
      reason: `${name}: reference did not resolve (archived, unpublished, or not in the entity store)`,
    };
  }

  if (contentType === "navigationLink") {
    const label = asText(fields.linkText);
    const externalUrl = asText(fields.externalUrl);
    const pageData = fields.pageData;
    // The SDK resolves the bound field one level deep, so Page Data usually
    // arrives as a Link stub with no fields. When it did resolve, its path is
    // as good as an External URL.
    const pagePath =
      isRecord(pageData) && isRecord(pageData.fields) ? asText(pageData.fields.path) : undefined;
    const href = externalUrl ?? pagePath;
    if (!label) return { reason: `${name}: Navigation Link has no Link Text` };
    if (!href) {
      return isRecord(pageData)
        ? {
            reason: `${name}: a Navigation Link that only points at a page through Page Data is one hop below what Studio resolves - link the Page entry directly, or give the link an External URL`,
          }
        : { reason: `${name}: Navigation Link has no External URL` };
    }
    const openInNewTab = fields.openInNewTab === true || fields.openInNewWindow === true;
    return { item: openInNewTab ? { label, href, openInNewTab } : { label, href } };
  }

  if (contentType === "page") {
    const label = asText(fields.breadcrumbTitle) ?? asText(fields.title);
    const href = asText(fields.path);
    if (!label) return { reason: `${name}: Page has no breadcrumb title or title` };
    if (!href) return { reason: `${name}: Page has no path` };
    return { item: { label, href } };
  }

  return { reason: `${name}: ${contentType} is not a Navigation Link or a Page` };
}

function mapRow(raw: unknown, index: number): Mapped {
  if (isEntryLike(raw)) return mapEntry(raw);
  if (!isRecord(raw)) return { reason: `item ${index + 1}: not a link entry` };
  const label = asText(raw.label);
  const href = asText(raw.href);
  if (!label || !href)
    return { reason: `item ${index + 1}: a link needs both a label and an href` };
  return {
    item: raw.openInNewTab === true ? { label, href, openInNewTab: true } : { label, href },
  };
}

function stripStagingOrigin(href: string): string {
  for (const origin of STAGING_WEB_ORIGINS) {
    if (href === origin) return "/";
    if (
      href.startsWith(`${origin}/`) ||
      href.startsWith(`${origin}?`) ||
      href.startsWith(`${origin}#`)
    ) {
      return href.slice(origin.length);
    }
  }
  return href;
}

/**
 * The origin a relative href is resolved against while it is checked. Only the
 * check uses it; the rendered href stays relative.
 */
const SAME_SITE_ORIGIN = "https://www.aspendental.com";

/**
 * A `..` path segment on its own, bounded by separators. The URL parser reads
 * a backslash as a slash in http(s) URLs, so it counts as a separator here
 * too. Two dots inside a file name (`a..b/file.pdf`) are not a segment.
 */
const TRAVERSAL_SEGMENT = /(^|[/\\])\.\.(?=$|[/\\?#])/;

/** The path of an href as the author wrote it: everything before the first ? or #. */
const pathAsWritten = (href: string): string => {
  const cut = href.search(/[?#]/);
  return cut < 0 ? href : href.slice(0, cut);
};

/** The scheme as written (lower-cased, with its colon), or undefined for a schemeless href. */
const schemeAsWritten = (href: string): string | undefined =>
  /^[a-z][a-z0-9+.-]*:/i.exec(href)?.[0].toLowerCase();

const SCHEMES_ALLOWED = new Set(["https:", "mailto:", "tel:"]);

/**
 * The reason an href may not render, or undefined when it may. Runs after the
 * web origins came off, on the string that would reach the anchor. An
 * allowlist, not a blocklist: a same-site path (one leading slash, resolving
 * to this site), a full https URL, or a mailto: / tel: link (the externalUrl
 * regexp on navigationLink admits both). Everything else is refused with a
 * reason the canvas can show: javascript:, data:, vbscript:, blob:, file: and
 * any other scheme, an http: downgrade, a schemeless host such as
 * www.example.com/x (the browser would read it as a path under this site), a
 * protocol-relative //host or /\host, and a path with a .. segment.
 */
function refuseHref(href: string): string | undefined {
  if (href.includes(SCHEDULING_URL_TOKEN)) {
    return "the [schedulingUrl] token is substituted by TAG's header, not by this atom";
  }
  let url: URL;
  try {
    url = new URL(href, SAME_SITE_ORIGIN);
  } catch {
    return "not a valid URL";
  }
  if (TRAVERSAL_SEGMENT.test(pathAsWritten(href))) {
    return "a traversing path (a .. segment) is refused";
  }
  if (
    url.hostname === ONETRUST_SENTINEL_HOST ||
    url.hostname.endsWith(`.${ONETRUST_SENTINEL_HOST}`)
  ) {
    return "the Your Privacy Choices control is the footer's own button, not a link";
  }
  if (href.startsWith("/")) {
    const second = href.charAt(1);
    if (second === "/" || second === "\\") {
      return "a protocol-relative href would leave the environment";
    }
    return url.origin === SAME_SITE_ORIGIN ? undefined : "a path that resolves off-site is refused";
  }
  const scheme = schemeAsWritten(href);
  if (!scheme) {
    return "a link must start with / (a same-site path) or https:// (a full URL)";
  }
  if (SCHEMES_ALLOWED.has(scheme) && url.protocol === scheme) return undefined;
  if (scheme === "http:") return "an http: link is refused, write it as https:";
  return `a ${scheme} link is refused: only a same-site path, an https URL, mailto: or tel: may render`;
}

/**
 * Resolves what Studio bound (or a caller passed) to the rows an atom renders.
 * Returns undefined for "nothing bound" - not an array, or an empty one - so
 * the caller falls back to its content-module defaults; otherwise the rows
 * that survived plus one reason per row that did not. `items` can be empty
 * when nothing survived, which callers also read as "use the defaults" while
 * still showing `dropped` on the canvas.
 */
export function toSmileLinkItems(
  value: unknown,
): { items: SmileLinkItem[]; dropped: string[] } | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const items: SmileLinkItem[] = [];
  const dropped: string[] = [];
  value.forEach((raw, index) => {
    const mapped = mapRow(raw, index);
    if ("reason" in mapped) {
      dropped.push(mapped.reason);
      return;
    }
    const href = stripStagingOrigin(normalizeBandHref(mapped.item.href));
    const refused = refuseHref(href);
    if (refused) {
      dropped.push(`${mapped.item.label}: ${refused}`);
      return;
    }
    items.push({ ...mapped.item, href });
  });
  return { items, dropped };
}
